import { DataTableRow } from '@/components/molecules/DataTableRow'
import { ListSearchInput, WorkshopSelect } from '@/components/molecules/ListFilters'
import { ListPagination } from '@/components/molecules/ListPagination'
import { SortableTableHead } from '@/components/molecules/SortableTableHead'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useListParams } from '@/hooks/useListParams'
import { formatDate } from '@/lib/utils'
import { fetchCars } from '@/services/cars'
import type { CarListQuery, WorkshopLocation } from '@backend/schemas'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

type SortField = CarListQuery['sortField']

export const CarsTable = () => {
  const navigate = useNavigate()
  const { params, update, setSort } = useListParams<SortField>({ sortField: 'updatedAt', sortDir: 'desc' })
  const query = { ...params, workshop: (params.workshop || undefined) as WorkshopLocation | undefined }

  const { data: cars, isLoading } = useQuery({
    queryKey: ['cars', query],
    queryFn: () => fetchCars(query),
    placeholderData: keepPreviousData,
  })

  const sortProps = {
    sortField: params.sortField,
    sortDir: params.sortDir,
    onSort: (field: SortField) => setSort(field, field === 'updatedAt' ? 'desc' : 'asc'),
    disabled: !!params.search,
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bilar</CardTitle>
        <div className="flex gap-4 mt-4">
          <ListSearchInput
            placeholder="Sök registreringsnummer (börjar med)..."
            value={params.search}
            onChange={search => update({ search })}
          />
          <WorkshopSelect value={params.workshop} onChange={workshop => update({ workshop })} />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead label="Registreringsnummer" field="registrationNumber" {...sortProps} />
                  <SortableTableHead label="Verkstad" field="workshop" {...sortProps} />
                  <SortableTableHead label="Senast uppdaterad" field="updatedAt" {...sortProps} />
                  <TableHead>Antal fakturor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cars?.items.length ? (
                  cars.items.map(car => (
                    <DataTableRow key={car.id} onClick={() => navigate(`/cars/${car.id}`)}>
                      <TableCell className="font-medium">{car.registrationNumber}</TableCell>
                      <TableCell className="capitalize">{car.workshop || '-'}</TableCell>
                      <TableCell>{formatDate(car.updatedAt)}</TableCell>
                      <TableCell>{car.invoices}</TableCell>
                    </DataTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      {params.search || params.workshop ? 'Inga bilar matchade sökningen' : 'Inga bilar hittades'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <ListPagination
              page={params.page}
              pageSize={cars?.pageSize ?? 100}
              total={cars?.total ?? 0}
              itemLabel="bilar"
              onPageChange={page => update({ page: String(page) })}
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}
